import { ReactNode } from 'react';

import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { Typography } from '@mui/material';

import Display from 'shared/components/Display';
import jsonObj from 'utils/objects';
import { generateRandomString } from 'utils/string';

import { ViewDetailsItemT } from '../ViewDetails';

const renderFieldValue = (value: any) => {
  switch (typeof value) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    default:
      if (!value && typeof value !== 'number') {
        return undefined;
      }
      return value;
  }
};

interface ViewDetailsRenderValueProps {
  name: string;
  value?: any;
  icon?: ReactNode;
  width?: string | number;
  link?: string;
}
export function ViewDetailsRenderValue({
  name,
  value,
  icon,
  width = '50rem',
  link,
}: ViewDetailsRenderValueProps) {
  return (
    <Display.CardWithIcon
      icon={icon || <ArrowRightIcon color="primary" />}
      name={name as string}
      value={renderFieldValue(value)}
      width={width}
      link={link}
    />
  );
}

interface RenderDefaultValueProps {
  dataStructure: ViewDetailsItemT;
  data: any;
}
function RenderDefaultValue({ dataStructure, data }: RenderDefaultValueProps) {
  const valueFromDataPath = dataStructure.externalValue
    || (dataStructure.dataPath && jsonObj.getDescendantProp(data, dataStructure.dataPath as string)
    );
  if (
    valueFromDataPath === undefined
    || (!valueFromDataPath && dataStructure.hideOnEmpty)
  ) return null;
  if (valueFromDataPath !== null && typeof valueFromDataPath === 'object') {
    if (Array.isArray(valueFromDataPath)) {
      if (valueFromDataPath.length === 0) return null;
      if (['string', 'number'].includes(typeof valueFromDataPath[0])) {
        return (
          <ViewDetailsRenderValue
            icon={dataStructure.icon}
            name={dataStructure.label as string}
            value={valueFromDataPath.join(', ')}
            link={dataStructure.linkFn ? dataStructure.linkFn(data) : ''}
          />
        );
      }
      return (
        <div className="view-details__sub-section">
          <Typography>{ dataStructure.label }</Typography>
          {
              valueFromDataPath.map((arrayValue, index) => (
                <Display.CardWithIcon
                  key={generateRandomString()}
                  icon={<ArrowRightIcon color="primary" />}
                  name={
                    dataStructure.renderFn
                      ? dataStructure.renderFn(arrayValue, index)
                      : Object.entries(arrayValue).map(([key, value]) => `${key}: ${value}`).join(' ')
                  }
                  width="60rem"
                  link={dataStructure.linkFn ? dataStructure.linkFn(data) : ''}
                />
              ))
            }
        </div>
      );
    }
    return (
      <div className="view-details__sub-section">
        <Typography>{ dataStructure.label }</Typography>
        {
            Object.entries(valueFromDataPath).map(([key, value]) => (
              <ViewDetailsRenderValue
                key={generateRandomString()}
                name={key}
                value={value as string}
              />
            ))
          }
      </div>
    );
  }
  return (
    <ViewDetailsRenderValue
      icon={dataStructure.icon}
      name={dataStructure.label as string}
      value={dataStructure.renderFn ? dataStructure.renderFn(valueFromDataPath) : valueFromDataPath}
      link={dataStructure.linkFn ? dataStructure.linkFn(data) : ''}
      width={dataStructure.maxWidth ? `${dataStructure.maxWidth}rem` : undefined}
    />
  );
}

export default RenderDefaultValue;
